import React from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faBrowser,
  faCircleQuestion,
  faCube,
  faDesktop,
  faGlobe,
  faMobileScreenButton,
  faServer,
  faTabletScreenButton,
  faVrCardboard
} from '@fortawesome/pro-solid-svg-icons'
import { publishedBuild } from './entry-story'

// The app an entry came from, in its meta row (owner 2026-10-08: "surface
// the app at a higher level in the timeline").
//
// The trail stores only the session's client claim (entry.app: "Admin",
// "Viewer", "Unknown", or nothing). Content's entries also carry
// requestApp, the request log's own answer for the entry's request: its
// stored label, version and platform ("iPhone app", "1.5.0", "iOS"), or,
// when it stored the request as unknown, the app it infers when the log is
// read (inferred, from: session | route | header; never stored). So:
//
//   1. the request's own app, with its version (platform on hover);
//   2. else the trail's app, as it is;
//   3. else (unknown or blank) the inferred app, marked "· inferred";
//   4. else "App unknown", muted -- except for a job or system action,
//      which shows nothing (the "automatic" pill already says what it is).
//
// Versions (owner 2026-10-09): an app's chip shows the app's own version
// ("iPhone app 1.5.0"); the editor's shows its caffeine package's ("EdXR
// Editor · caffeine 1.1.23"), from the request's own header
// (requestApp.caffeineVersion, sent since caffeine 2026-10-09), else from
// the build it published (entry-story publishedBuild: the caffeine the
// editor wrote into it). Never an editor's requestApp.version: an older
// editor sent its Unity project's version string there. The full value
// adds the caffeine and Unity the app or editor was built with and its
// platform: "iPhone app 1.5.0 · caffeine 1.1.24 · Unity 6000.0.63f1 · iOS".
// No version known: the chip is as it was.
//
// The full value is the chip's title and the App row of Details.

const UNKNOWN = new Set(['', 'unknown', 'unknown client', 'unknown browser'])

export const isUnknownApp = app => UNKNOWN.has(String(app || '').trim().toLowerCase())

const ICONS = {
  desktop: faDesktop,
  browser: faBrowser,
  phone: faMobileScreenButton,
  tablet: faTabletScreenButton,
  vr: faVrCardboard,
  web: faGlobe,
  server: faServer,
  app: faCube,
  unknown: faCircleQuestion
}

// The family of an app's name: core clientapp's labels, the Request log's
// inferred ones ("Browser sign-in (SSO)", "Viewer app (device unknown)")
// and the trail's own claims (admin, editor, viewer, launch). A name it
// doesn't know is a plain app.
export const appFamily = name => {
  const n = String(name || '').trim().toLowerCase()
  if (isUnknownApp(n)) return 'unknown'
  if (n.includes('editor') || /^(mac|windows|linux) app/.test(n)) return 'desktop'
  if (/quest|pico|vive|headset/.test(n)) return 'vr'
  if (/ipad|tablet/.test(n)) return 'tablet'
  if (/iphone|android|phone/.test(n) || n === 'ios') return 'phone'
  if (n === 'web viewer' || n === 'launch') return 'web'
  if (/admin|browser/.test(n) || n === 'web') return 'browser'
  if (/server|webhook|sendgrid|postman/.test(n)) return 'server'
  return 'app'
}

const FROM_TEXT = {
  session: 'inferred from session',
  route: 'inferred from route',
  header: 'from its app header'
}

// The editor, as content's label ("EdXR Editor") or the trail's claim
// ("Editor") names it.
export const isEditorApp = name => /\beditor\b/i.test(String(name || ''))

// Unity's editor platforms, which core leaves as they were sent.
const PLATFORMS = { OSXEditor: 'macOS', WindowsEditor: 'Windows', LinuxEditor: 'Linux' }
const platformText = p => (p ? PLATFORMS[String(p).trim()] || String(p).trim() : null)

const sameApp = (a, b) => a === b || (isEditorApp(a) && isEditorApp(b))

// The caffeine and Unity versions the request's own header named, on this
// entry or another of its story with the same app.
const headerVersions = (entries, name) => {
  for (const e of entries) {
    const r = e && e.requestApp
    if (r && sameApp(r.app, name) && (r.caffeineVersion || r.unityVersion)) return { caffeine: r.caffeineVersion || null, unity: r.unityVersion || null }
  }
  return { caffeine: null, unity: null }
}

// name, its version and platform as the request names them -> what the
// chip says (text) and the full value.
const describe = ({ name, version, platform }, entries) => {
  const editor = isEditorApp(name)
  const header = headerVersions(entries, name)
  const build = editor && !(header.caffeine && header.unity) ? publishedBuild(entries) || {} : {}
  const caffeineVersion = header.caffeine || build.caffeineVersion || null
  const unityVersion = header.unity || build.unityVersion || null
  const shown = editor ? (caffeineVersion ? `caffeine ${caffeineVersion}` : null) : version || null
  const versionText = shown && editor ? `· ${shown}` : shown
  const text = shown ? `${name} ${versionText}` : name
  const detail = [text, !editor && caffeineVersion ? `caffeine ${caffeineVersion}` : null, unityVersion ? `Unity ${unityVersion}` : null, platformText(platform)]
    .filter(Boolean)
    .join(' · ')
  return { name, version: shown, versionText, text, detail, caffeineVersion, unityVersion }
}

// The chip's view of an entry: { name, version (what follows the name:
// "1.5.0", "caffeine 1.1.23"), versionText (as drawn), text (the two
// together), mark, full, title, family, inferred, unknown, caffeineVersion,
// unityVersion }, or null when there is nothing to show. automatic: the
// actor is a job, the system or no one (actorView's automatic or
// unattributed). related: the entry's story (a publish's entries), whose
// headers and published build name versions too.
export const appChipView = (entry, { automatic = false, related = null } = {}) => {
  const req = (entry && entry.requestApp) || null
  const app = entry && entry.app
  const entries = [entry, ...(related || []).filter(e => e && e !== entry)]
  if (req && req.app && !req.inferred) {
    const d = describe({ name: req.app, version: req.version, platform: req.platform }, entries)
    return { ...d, mark: null, full: d.detail, title: `App: ${d.detail}`, family: appFamily(req.app), inferred: false, unknown: false }
  }
  if (!isUnknownApp(app)) {
    const d = describe({ name: app }, entries)
    return { ...d, mark: null, full: d.detail, title: `App: ${d.detail}`, family: appFamily(app), inferred: false, unknown: false }
  }
  if (req && req.app) {
    const d = describe({ name: req.app, version: req.version, platform: req.platform }, entries)
    const from = FROM_TEXT[req.from] || 'inferred'
    return {
      ...d,
      mark: '· inferred',
      full: `${d.detail} · ${from} (not stored)`,
      title: `${d.detail} · ${from}. The app is inferred when the request log is read and is not stored; this action recorded ${app ? `"${app}"` : 'no app'}.`,
      family: appFamily(req.app),
      inferred: true,
      unknown: false
    }
  }
  if (automatic) return null
  return {
    name: 'App unknown',
    version: null,
    versionText: null,
    text: 'App unknown',
    mark: null,
    full: app || 'Not recorded',
    title: app ? `App unknown: this action recorded "${app}"` : 'App unknown: this action recorded no app',
    family: 'unknown',
    inferred: false,
    unknown: true,
    caffeineVersion: null,
    unityVersion: null
  }
}

// The workspace pill (.sw-pill) with the family's icon in place of its dot.
// On a narrow row the version gives way first (the editor's "· caffeine …"
// with its dot), then the name (each with an ellipsis); the icon and the
// "· inferred" mark never do.
export const AppChip = ({ view }) =>
  <span className={`sw-pill au-app${view.inferred ? ' au-app-inferred' : ''}${view.unknown ? ' au-app-unknown' : ''}`} title={view.title}>
    <FontAwesomeIcon icon={ICONS[view.family] || faCube} aria-hidden="true" />
    <span className="au-app-name">{view.name}</span>
    {view.versionText && <>{' '}<span className="au-app-ver">{view.versionText}</span></>}
    {view.mark && <>{' '}<span className="au-app-mark">{view.mark}</span></>}
  </span>
